
import { useState, FormEvent } from 'react';

const FORM_URL = 'https://readdy.ai/api/form/d6cm76rv53dtlm7j86jg';

const SERVICE_OPTIONS = [
  'Brand Strategy & Identity',
  'Film & Video Production',
  'Outdoor Advertising',
  'Digital Marketing',
  'Garment Manufacturing',
  'Web Development',
  'Social Media Management',
  'Corporate Communications',
  'Other',
];

const BUDGET_OPTIONS = [
  'Under $1,000',
  '$1,000 – $5,000',
  '$5,000 – $15,000',
  '$15,000 – $50,000',
  '$50,000+',
  'Not sure yet',
];

export default function ContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    budget: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');

    if (!formData.name || !formData.email || !formData.message) {
      setError('Please fill in all required fields.');
      return;
    }

    if (formData.message.length > 500) {
      setError('Message must be 500 characters or less.');
      return;
    }

    setSubmitting(true);

    try {
      const body = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        if (value) body.append(key, value);
      });

      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      if (res.ok) {
        setSubmitted(true);
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          service: '',
          budget: '',
          message: '',
        });
      } else {
        setError('Something went wrong. Please try again.');
      }
    } catch {
      setError(
        'Network error. Please check your connection and try again.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8 sm:p-10 text-center">
        <div className="w-16 h-16 bg-[#038C7F]/10 rounded-full flex items-center justify-center mx-auto mb-5">
          <i className="ri-check-double-line text-3xl text-[#038C7F]"></i>
        </div>
        <h3 className="text-xl font-bold text-[#0D0D0D] mb-2">
          Message Sent Successfully!
        </h3>
        <p className="text-sm text-gray-500 mb-6 max-w-sm mx-auto">
          Thank you for reaching out. Our team will get back to you within 24
          hours.
        </p>
        <button
          onClick={() => setSubmitted(false)}
          className="px-6 py-2.5 bg-[#038C7F] text-white text-sm font-semibold rounded-full hover:bg-[#026b61] transition-colors cursor-pointer whitespace-nowrap"
        >
          Send Another Message
        </button>
      </div>
    );
  }

  return (
    <form
      id="contact-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 sm:p-8 lg:p-10"
    >
      <div className="mb-6">
        <h3 className="text-lg font-bold text-[#0D0D0D] mb-1">
          Send Us a Message
        </h3>
        <p className="text-xs text-gray-400">
          Fields marked with * are required
        </p>
      </div>

      {error && (
        <div className="mb-5 px-4 py-3 bg-red-50 border border-red-100 rounded-lg flex items-center gap-2">
          <i className="ri-error-warning-line text-red-500 text-sm"></i>
          <span className="text-xs text-red-600">{error}</span>
        </div>
      )}

      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label
              htmlFor="name"
              className="block text-xs font-semibold text-gray-600 mb-1.5"
            >
              Full Name *
            </label>
            <div className="relative">
              <i className="ri-user-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
              <input
                id="name"
                name="name"
                type="text"
                value={formData.name}
                onChange={handleChange}
                placeholder="John Doe"
                required
                className="w-full pl-9 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:border-[#038C7F] focus:ring-1 focus:ring-[#038C7F]/20 transition-all"
              />
            </div>
          </div>
          <div>
            <label
              htmlFor="email"
              className="block text-xs font-semibold text-gray-600 mb-1.5"
            >
              Email Address *
            </label>
            <div className="relative">
              <i className="ri-mail-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
              <input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="john@company.com"
                required
                className="w-full pl-9 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:border-[#038C7F] focus:ring-1 focus:ring-[#038C7F]/20 transition-all"
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label
              htmlFor="phone"
              className="block text-xs font-semibold text-gray-600 mb-1.5"
            >
              Phone Number
            </label>
            <div className="relative">
              <i className="ri-phone-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
              <input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+256 700 000 000"
                className="w-full pl-9 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:border-[#038C7F] focus:ring-1 focus:ring-[#038C7F]/20 transition-all"
              />
            </div>
          </div>
          <div>
            <label
              htmlFor="company"
              className="block text-xs font-semibold text-gray-600 mb-1.5"
            >
              Company / Organization
            </label>
            <div className="relative">
              <i className="ri-building-2-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
              <input
                id="company"
                name="company"
                type="text"
                value={formData.company}
                onChange={handleChange}
                placeholder="Your Company"
                className="w-full pl-9 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:border-[#038C7F] focus:ring-1 focus:ring-[#038C7F]/20 transition-all"
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label
              htmlFor="service"
              className="block text-xs font-semibold text-gray-600 mb-1.5"
            >
              Service Interested In
            </label>
            <div className="relative">
              <i className="ri-service-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
              <select
                id="service"
                name="service"
                value={formData.service}
                onChange={handleChange}
                className="w-full pl-9 pr-8 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-800 focus:outline-none focus:border-[#038C7F] focus:ring-1 focus:ring-[#038C7F]/20 transition-all appearance-none cursor-pointer"
              >
                <option value="">Select a service</option>
                {SERVICE_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
              <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none"></i>
            </div>
          </div>
          <div>
            <label
              htmlFor="budget"
              className="block text-xs font-semibold text-gray-600 mb-1.5"
            >
              Estimated Budget
            </label>
            <div className="relative">
              <i className="ri-money-dollar-circle-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
              <select
                id="budget"
                name="budget"
                value={formData.budget}
                onChange={handleChange}
                className="w-full pl-9 pr-8 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-800 focus:outline-none focus:border-[#038C7F] focus:ring-1 focus:ring-[#038C7F]/20 transition-all appearance-none cursor-pointer"
              >
                <option value="">Select budget range</option>
                {BUDGET_OPTIONS.map((b) => (
                  <option key={b} value={b}>
                    {b}
                  </option>
                ))}
              </select>
              <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none"></i>
            </div>
          </div>
        </div>

        <div>
          <label
            htmlFor="message"
            className="block text-xs font-semibold text-gray-600 mb-1.5"
          >
            Your Message *{' '}
            <span className="font-normal text-gray-400">
              ({formData.message.length}/500)
            </span>
          </label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            placeholder="Tell us about your project, goals, and timeline..."
            required
            maxLength={500}
            rows={5}
            className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:border-[#038C7F] focus:ring-1 focus:ring-[#038C7F]/20 transition-all resize-none"
          ></textarea>
        </div>
      </div>

      <button
        type="submit"
        disabled={submitting}
        className="mt-6 w-full group relative px-8 py-3.5 bg-[#038C7F] text-white text-sm font-bold rounded-full overflow-hidden whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
      >
        <span className="relative z-10 flex items-center justify-center gap-2">
          {submitting ? (
            <>
              <i className="ri-loader-4-line animate-spin text-base"></i>
              Sending...
            </>
          ) : (
            <>
              Send Message
              <i className="ri-send-plane-2-line text-base group-hover:translate-x-1 transition-transform"></i>
            </>
          )}
        </span>
        <div className="absolute inset-0 bg-[#F2780C] translate-y-full group-hover:translate-y-0 transition-transform duration-300"></div>
      </button>
    </form>
  );
}
